import {
  addBankAccountPaymentMethod,
} from "../services/api";
import { PayoutMethodType, type PayoutInput } from "../interfaces/payout";
import { registerPayoutCard } from "./register-payout-card";

/**
 * Resolves `payoutPaymentMethodId` for create-operation, mirroring smart's
 * commit helpers. Saved instruments reuse `paymentMethodId`; new bank/card
 * payouts are registered first. Cash / mobile wallet return `undefined`.
 */
export const resolvePayoutPaymentMethodId = async (
  linkId: string,
  payoutMethod: PayoutMethodType,
  payout: PayoutInput,
  destinationCountry: string,
  cardholderName?: string
): Promise<string | undefined> => {
  if (payoutMethod === PayoutMethodType.PUSH_TO_CARD) {
    if (
      payout.paymentMethodId &&
      (payout.source === "saved" || !payout.cardPan)
    ) {
      return payout.paymentMethodId;
    }
    if (
      payout.cvsValidationId ||
      payout.numberToken ||
      payout.cardPan?.trim()
    ) {
      const registered = await registerPayoutCard(
        linkId,
        payout,
        cardholderName
      );
      return registered.paymentMethodId;
    }
    throw new Error("Push-to-card payout requires a validated card");
  }

  if (payoutMethod === PayoutMethodType.BANK_ACCOUNT) {
    if (payout.paymentMethodId && (payout.source === "saved" || !payout.accountNumber)) {
      return payout.paymentMethodId;
    }
    if (!payout.accountNumber?.trim()) {
      throw new Error("Bank payout requires an account number");
    }
    return registerBankPayoutInstrument(linkId, payout, destinationCountry);
  }

  // CASH_PICKUP / MOBILE_WALLET / HOME_DELIVERY — no stored instrument.
  return undefined;
};

/**
 * After a successful register, strip raw PAN/CLABE from the persisted draft
 * and keep only the stored instrument id + display fields.
 */
export const sanitizePayoutAfterRegister = (
  payout: PayoutInput,
  paymentMethodId: string
): PayoutInput => {
  const last4 =
    payout.last4 ||
    payout.accountNumber?.replace(/\D/g, "").slice(-4) ||
    payout.cardPan?.replace(/\D/g, "").slice(-4);

  return {
    source: "saved",
    paymentMethodId,
    bankId: payout.bankId,
    brand: payout.brand,
    last4,
    cardExpiry: payout.cardExpiry,
    cardholderName: payout.cardholderName,
    stateCode: payout.stateCode,
    payerId: payout.payerId,
    carrierId: payout.carrierId,
    phone: payout.phone,
  };
};

const registerBankPayoutInstrument = async (
  linkId: string,
  payout: PayoutInput,
  destinationCountry: string
): Promise<string> => {
  const registered = await addBankAccountPaymentMethod(linkId, {
    countryCode: (destinationCountry || "MX").toUpperCase(),
    bankName: payout.bankId ?? "UNKNOWN",
    accountNumber: payout.accountNumber!,
  });
  return registered.paymentMethodId;
};
